<script setup lang="ts">
    import { reactive, inject, computed, onMounted } from 'vue';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import Loading from '@shared/components/Loading.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import { bsAlert, AlertTypes } from '@cwd/shared/components/BsAlerts';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SubscriptionAccess } from '@views/BackOffice/Subscription/Subscription.access';
    import { FormEl } from '@cwd/shared/components/Form/Form';
    import { NewAccountRequest } from '../../User/AccountReference';
    import * as accountService from '../../Account/Account.access';

    const props = defineProps<{
        onComplete?: () => void;
    }>();

    const emit = defineEmits(['cancelled']);

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);

    const isInputValid = (inputValue: string): boolean => {
        return !!inputValue && inputValue.trim().length > 0;
    };

    const state = reactive<{
        creatingAccount: boolean;
        useParentSubdomain: boolean;
        customSubdomain: string;
        billedByChargebee: boolean;
        isTimeLimited: boolean;
        start: string;
        end: string;
        newAccountForm: {
            data: FormEl;
        };
    }>({
        creatingAccount: false,
        useParentSubdomain: true,
        customSubdomain: '',
        billedByChargebee: false,
        isTimeLimited: false,
        start: '',
        end: '',
        newAccountForm: {
            data: {
                AccountName: {
                    modelValue: '',
                    input: {
                        label: 'Account Name',
                        type: 'text' as const,
                        maxlength: 255,
                        placeholder: 'Enter Account Name',
                    },
                    required: true,
                    isValid: false,
                    invalidMessage: 'Account name is required',
                },
                DefaultReplyToAddress: {
                    modelValue: '',
                    input: {
                        label: 'Default ReplyTo-Address',
                        type: 'text' as const,
                        maxlength: 100,
                        placeholder: 'Enter ReplyTo-Address',
                    },
                    required: true,
                    isValid: false,
                    invalidMessage: 'Default ReplyTo-Address is required',
                },
                DefaultName: {
                    modelValue: '',
                    input: {
                        label: 'Default from name',
                        type: 'text' as const,
                        maxlength: 100,
                        placeholder: 'Enter Default from name',
                    },
                    required: true,
                    isValid: false,
                    invalidMessage: 'Default from name is required',
                },
            },
        },
    });

    // Only a Chargebee-billed subscription gets the option; every other keeps the old flow (Start/End on the account).
    onMounted(async () => {
        const serviceSubscriptionId = subscriptionAccess.state.boServiceSubscription?.ServiceSubscriptionId;
        if (!serviceSubscriptionId) return;
        try {
            state.billedByChargebee = (await accountService.getBillingOwner(serviceSubscriptionId)) === 'Chargebee';
        } catch {
            state.billedByChargebee = false;
        }
    });

    const parentPublicHost = computed((): string => {
        const accounts = subscriptionAccess.state.accounts;
        if (!accounts || accounts.length === 0) return '';
        const oldest = [...accounts].sort((a, b) => new Date(a.Created).getTime() - new Date(b.Created).getTime())[0];
        return oldest?.PublicHost ?? '';
    });

    function handleInput(key: string, newVal: any) {
        var input = state.newAccountForm.data[key as keyof typeof state.newAccountForm.data];
        input.modelValue = newVal;
        input.isValid = isInputValid(newVal);
    }

    function isFormValid(): { hasErrors: boolean; errorKeys: string[] } {
        var hasErrors = false;
        var errorKeys: string[] = [];
        for (const [key, value] of Object.entries(state.newAccountForm.data)) {
            if (value.required && !value.isValid) {
                hasErrors = true;
                errorKeys.push(key);
            }
        }
        return { hasErrors, errorKeys };
    }

    function cancel(): void {
        state.newAccountForm.data.AccountName.modelValue = '';
        state.newAccountForm.data.AccountName.isValid = false;
        state.newAccountForm.data.DefaultReplyToAddress.modelValue = '';
        state.newAccountForm.data.DefaultName.modelValue = '';
        state.useParentSubdomain = true;
        state.customSubdomain = '';
        state.isTimeLimited = false;
        state.start = '';
        state.end = '';

        emit('cancelled');
    }

    async function createAccount(): Promise<void> {
        if (!state.useParentSubdomain && !state.customSubdomain.trim()) {
            bsAlert({
                type: AlertTypes.Danger,
                title: 'Validation Error',
                content: 'Please enter a subdomain or use the parent account subdomain.',
            });
            return;
        }

        // Chargebee F2b: a time-limited account is billed once for Start–End (End exclusive, as Account.End).
        if (state.billedByChargebee && state.isTimeLimited && (!state.start || !state.end || state.end <= state.start)) {
            bsAlert({
                type: AlertTypes.Danger,
                title: 'Validation Error',
                content: 'A time-limited account needs a start date and an end date after it.',
            });
            return;
        }

        const formValidationResult = isFormValid();
        if (formValidationResult.hasErrors) {
            formValidationResult.errorKeys.forEach((key) => {
                bsAlert({
                    type: AlertTypes.Danger,
                    title: 'Validation Error',
                    content: state.newAccountForm.data[key as keyof typeof state.newAccountForm.data].invalidMessage!,
                });
            });
            return;
        }

        state.creatingAccount = true;

        try {
            if (!subscriptionAccess.state.boServiceSubscription) {
                throw new Error('Insufficient user rights to perform this action');
            }

            const payload: NewAccountRequest = {
                Name: state.newAccountForm.data.AccountName.modelValue,
                ClientCompanyId: subscriptionAccess.state.boServiceSubscription.ClientCompanyId,
                DefaultReplyToAddress: state.newAccountForm.data.DefaultReplyToAddress.modelValue || '',
                DefaultFromName: state.newAccountForm.data.DefaultName.modelValue || '',
                CustomPublicHost: state.useParentSubdomain ? null : state.customSubdomain || null,
                IsTimeLimited: state.billedByChargebee && state.isTimeLimited,
                Start: state.billedByChargebee && state.isTimeLimited ? state.start : null,
                End: state.billedByChargebee && state.isTimeLimited ? state.end : null,
            };

            await accountService.createAccount(payload);

            await subscriptionAccess.getClientCompanyAccounts();

            bsAlert({
                title: 'Success',
                content: 'Account created successfully',
            });

            props.onComplete?.();

            cancel();
        } catch (error) {
            bsAlert({
                type: AlertTypes.Danger,
                title: 'Error',
                content: 'Failed to create account: ' + (error instanceof Error ? error.message : 'Unknown error'),
            });
        } finally {
            state.creatingAccount = false;
        }
    }
</script>

<template>
    <h3 class="h4">Create account</h3>

    <Loading :show="state.creatingAccount" loadingStyle="progress-bar" :fixed="true" />

    <div v-if="!state.creatingAccount">
        <Form
            class="wrapper-grid"
            :data="state.newAccountForm.data"
            :submitting="state.creatingAccount"
            :hide-submit="true"
            @update:modelValue="handleInput"></Form>

        <div class="form-group" v-if="parentPublicHost">
            <FormCheckable
                :label="`Same subdomain as main account (${parentPublicHost})`"
                :switchStyle="SwitchStyle.Standard"
                :checked="state.useParentSubdomain"
                @update:modelValue="(newVal: boolean) => (state.useParentSubdomain = newVal)"></FormCheckable>
        </div>

        <FormInput
            v-if="!state.useParentSubdomain"
            class="form-group"
            v-model:modelValue="state.customSubdomain"
            label="Custom subdomain"
            placeholder="e.g. ronneby-kommun.ungapped.io"></FormInput>

        <div class="form-group" v-if="state.billedByChargebee">
            <FormCheckable
                label="Time-limited account"
                description="Billed once for its period, never as an extra account. Can be made ordinary later, not the other way."
                :switchStyle="SwitchStyle.Standard"
                :checked="state.isTimeLimited"
                @update:modelValue="(newVal: boolean) => (state.isTimeLimited = newVal)"></FormCheckable>
        </div>

        <template v-if="state.billedByChargebee && state.isTimeLimited">
            <FormInput class="form-group" v-model:modelValue="state.start" label="Start" type="date" :required="true"></FormInput>
            <FormInput class="form-group" v-model:modelValue="state.end" label="End (first day not included)" type="date" :required="true"></FormInput>
        </template>
    </div>

    <div class="form-group">
        <button type="button" class="btn btn-default m-xs-r" @click.prevent="cancel">Cancel</button>
        <button type="button" class="btn btn-primary" @click.prevent="createAccount" v-bind:disabled="state.creatingAccount">Create</button>
    </div>
</template>
